// Per-day session override dialog: cancel / move / relocate ONE date of a
// session block without touching the weekly template.
//
// Opened two ways:
//   • Sessions page (Week mode): with a concrete {occurrence, block}.
//   • Calendar day view: with {doctorId, date} — the dialog self-fetches that
//     doctor's occurrences for the date and shows a picker when there are
//     several sessions that day.
//
// Conflict protocol: the first submit uses onConflict:"abort"; a 409 comes
// back with the overlapping appointments and nothing written — that response
// renders as the conflict step, where staff picks "also move these
// appointments" (onConflict:"move") or "leave them" ("ignore").
//
// Undo: reverting posts :revertOverride. Overridden chips reopen the dialog
// in "revert" framing.

import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import {
  ArrowLeftRight,
  ArrowRight,
  Ban,
  CalendarClock,
  RotateCcw,
  TriangleAlert,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import {
  applySessionOverride,
  fetchSessionOccurrences,
  revertSessionOverride,
} from "@/lib/api/admin-session-blocks";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import { ComboboxField, dateToYmd, ymdToDate } from "../day-view/form-fields";
import { chipTone } from "./sessions-utils";

const NOTE_QUICK_PICKS = [
  "Doctor unavailable",
  "Clinic closed",
  "Covering another clinic",
  "Public holiday",
];

const ACTIONS = [
  {
    id: "cancel",
    icon: Ban,
    title: "Cancel this day",
    description: "The session is removed for this date only; the weekly pattern is untouched.",
  },
  {
    id: "move",
    icon: ArrowRight,
    title: "Move to another date",
    description: "The whole session (and its open slots) shifts to a different day.",
  },
  {
    id: "relocate",
    icon: ArrowLeftRight,
    title: "Change location for this day",
    description: "Same day and time, but the doctor sits at a different clinic.",
  },
];

function occurrenceSummary(occ, doctorName, tenantName) {
  const day = occ?.startAt?.isValid ? occ.startAt.toFormat("cccc d LLL") : occ?.date || "";
  const range =
    occ?.startAt?.isValid && occ?.endAt?.isValid
      ? `${occ.startAt.toFormat("HH:mm")}–${occ.endAt.toFormat("HH:mm")}`
      : "";
  return { day, range, doctorName, tenantName };
}

function ConflictRow({ conflict }) {
  const start = DateTime.fromISO(String(conflict?.start || ""));
  const end = DateTime.fromISO(String(conflict?.end || ""));
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-xs">
      <CalendarClock className="size-3.5 shrink-0 text-muted-foreground" />
      <span className="font-mono tabular-nums text-foreground/90">
        {start.isValid ? start.toFormat("HH:mm") : "?"}–{end.isValid ? end.toFormat("HH:mm") : "?"}
      </span>
      <span className="min-w-0 flex-1 truncate">{conflict?.title || "Appointment"}</span>
      {conflict?.recurring ? (
        <span className="shrink-0 rounded-sm bg-amber-500/15 px-1 py-0.5 text-[9px] font-semibold uppercase text-amber-700 dark:text-amber-300">
          recurring
        </span>
      ) : null}
    </div>
  );
}

export function SessionOverrideDialog({
  open,
  onOpenChange,
  occurrence = null,
  block = null,
  doctorId = "",
  date = "",
  tenantOptions = [],
  doctorOptions = [],
  applyLocalOverride,
  revertLocalOverride,
  onApplied,
}) {
  const [pickedOccurrence, setPickedOccurrence] = useState(null);
  const [dayOccurrences, setDayOccurrences] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [action, setAction] = useState("cancel");
  const [toDate, setToDate] = useState(null); // JS Date
  const [toTenantId, setToTenantId] = useState("");
  const [note, setNote] = useState("");
  const [conflicts, setConflicts] = useState(null); // null = not checked yet
  const [moveAppointments, setMoveAppointments] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const target = occurrence || pickedOccurrence;
  const existingOverride = target?.override || null;

  // Reset per open.
  useEffect(() => {
    if (!open) return;
    setPickedOccurrence(null);
    setAction("cancel");
    setToDate(null);
    setToTenantId("");
    setNote("");
    setConflicts(null);
    setMoveAppointments(false);
    setSubmitting(false);
    setSubmitError("");
  }, [open]);

  // Day-view entry: self-fetch that doctor+date's sessions.
  useEffect(() => {
    if (!open || occurrence || !doctorId || !date) return;
    let isMounted = true;
    setDayLoading(true);
    fetchSessionOccurrences({ from: date, to: date, doctorIds: [doctorId] })
      .then((response) => {
        if (!isMounted) return;
        const items = (Array.isArray(response?.items) ? response.items : []).map((item) => {
          const startAt = item?.start ? DateTime.fromISO(item.start) : null;
          const endAt = item?.end ? DateTime.fromISO(item.end) : null;
          return {
            key: `${item.blockId}:${item.date}:${item.suppressed ? "src" : "eff"}`,
            blockId: String(item.blockId || ""),
            date: String(item.date || ""),
            doctorId: String(item.doctorId || ""),
            tenantId: String(item.tenantId || ""),
            baseTenantId: String(item.baseTenantId || ""),
            label: String(item.label || ""),
            startAt: startAt?.isValid ? startAt : null,
            endAt: endAt?.isValid ? endAt : null,
            colorHex: String(item.colorHex || ""),
            suppressed: Boolean(item.suppressed),
            override: item?.override || null,
          };
        });
        setDayOccurrences(items);
        if (items.length === 1) setPickedOccurrence(items[0]);
      })
      .catch(() => {
        if (isMounted) setDayOccurrences([]);
      })
      .finally(() => {
        if (isMounted) setDayLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [open, occurrence, doctorId, date]);

  const names = useMemo(() => {
    const doctors = new Map(doctorOptions.map((opt) => [String(opt.id), opt.name]));
    const tenants = new Map(tenantOptions.map((opt) => [String(opt.id), opt.name]));
    return {
      doctor: (id) => doctors.get(String(id)) || String(id || ""),
      tenant: (id) => tenants.get(String(id)) || String(id || ""),
    };
  }, [doctorOptions, tenantOptions]);

  const summary = target
    ? occurrenceSummary(target, names.doctor(target.doctorId), names.tenant(target.baseTenantId || target.tenantId))
    : null;

  const tenantChoices = useMemo(
    () =>
      tenantOptions
        .filter((opt) => String(opt.id) !== String(target?.baseTenantId || target?.tenantId || ""))
        .map((opt) => ({ value: String(opt.id), label: opt.name })),
    [tenantOptions, target],
  );

  // The date being overridden is the occurrence's SOURCE date (for moved-in
  // chips that's the origin, so a re-override edits the same exception).
  const overrideDate = existingOverride?.sourceDate || target?.date || "";
  const blockId = target?.blockId || block?.id || "";

  // Default move target: the NEXT day, not next week — a weekly session fires
  // again on the same weekday next week, so that date would collide with the
  // block's own cadence and be rejected.
  const defaultMoveTarget = useMemo(() => {
    if (!overrideDate) return null;
    const dt = DateTime.fromISO(overrideDate).plus({ days: 1 });
    return dt.isValid ? dt.toJSDate() : null;
  }, [overrideDate]);

  const submit = async (onConflict) => {
    if (!blockId || !overrideDate) return;
    const body = {
      date: overrideDate,
      action,
      note: note.trim(),
      onConflict,
    };
    if (action === "move") {
      body.toDate = dateToYmd(toDate || defaultMoveTarget);
      if (!body.toDate) {
        setSubmitError("Pick the date to move the session to.");
        return;
      }
    }
    if (action === "relocate") {
      if (!toTenantId) {
        setSubmitError("Pick the clinic to relocate to.");
        return;
      }
      body.toTenantId = toTenantId;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await applySessionOverride({ blockId, body });
      const movedCount = Array.isArray(response?.movedAppointments)
        ? response.movedAppointments.length
        : 0;
      const unmovable = Array.isArray(response?.unmovableConflicts)
        ? response.unmovableConflicts.length
        : 0;

      applyLocalOverride?.(blockId, overrideDate, {
        action,
        toDate: body.toDate,
        toTenantId: body.toTenantId,
        note: body.note,
      });

      const dayLabel = DateTime.fromISO(overrideDate).toFormat("ccc d LLL");
      const actionLabel =
        action === "cancel"
          ? `Session cancelled for ${dayLabel}`
          : action === "move"
            ? `Session moved ${dayLabel} → ${DateTime.fromISO(body.toDate).toFormat("ccc d LLL")}`
            : `Session relocated for ${dayLabel}`;
      toast.success(actionLabel, {
        id: `session-override-${blockId}-${overrideDate}`,
        duration: 8000,
        description:
          movedCount > 0
            ? `${movedCount} appointment${movedCount === 1 ? "" : "s"} moved along (history recorded).${unmovable ? ` ${unmovable} recurring left in place.` : ""}`
            : unmovable
              ? `${unmovable} recurring appointment${unmovable === 1 ? "" : "s"} left in place.`
              : undefined,
        action:
          movedCount === 0
            ? {
                label: "Undo",
                onClick: async () => {
                  try {
                    await revertSessionOverride({ blockId, date: overrideDate });
                    revertLocalOverride?.(blockId, overrideDate);
                    publishCalendarRefresh({ source: "session-override-undo" });
                    onApplied?.();
                  } catch (error) {
                    toast.error(error instanceof Error ? error.message : "Failed to undo the override.");
                  }
                },
              }
            : undefined,
      });
      publishCalendarRefresh({ source: "session-override" });
      onApplied?.();
      onOpenChange?.(false);
    } catch (error) {
      if (error?.status === 409 && Array.isArray(error?.payload?.conflicts)) {
        setConflicts(error.payload.conflicts);
        setMoveAppointments(false);
      } else {
        revertLocalOverride?.(blockId, overrideDate);
        setSubmitError(error instanceof Error ? error.message : "Failed to apply the override.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevert = async () => {
    if (!blockId || !overrideDate) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const response = await revertSessionOverride({ blockId, date: overrideDate });
      revertLocalOverride?.(blockId, overrideDate);
      applyLocalOverride?.(blockId, overrideDate, null);
      const movedIds = Array.isArray(response?.movedAppointmentIds) ? response.movedAppointmentIds : [];
      toast.success("Override removed — regular schedule restored", {
        description:
          movedIds.length > 0
            ? `${movedIds.length} appointment${movedIds.length === 1 ? "" : "s"} were moved with the original override and stay where they are — reschedule them from the calendar if needed.`
            : undefined,
        duration: 8000,
      });
      publishCalendarRefresh({ source: "session-override-revert" });
      onApplied?.();
      onOpenChange?.(false);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to remove the override.");
    } finally {
      setSubmitting(false);
    }
  };

  const showPicker = !occurrence && !pickedOccurrence;
  const movableConflicts = (conflicts || []).filter((c) => !c.recurring);

  return (
    <Dialog open={open} onOpenChange={(next) => (!submitting ? onOpenChange?.(next) : null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Session override</DialogTitle>
          <DialogDescription>
            One-day exception — the recurring template stays as it is.
          </DialogDescription>
        </DialogHeader>

        {showPicker ? (
          <div className="space-y-2">
            {dayLoading ? (
              <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                <Spinner className="size-4" /> Loading sessions…
              </div>
            ) : dayOccurrences.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-5 text-center text-sm text-muted-foreground">
                No session blocks for this doctor on {date}. Manage sessions on the Sessions page.
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground">Pick the session to override:</p>
                {dayOccurrences.map((occ) => {
                  const tone = chipTone(occ.tenantId, occ.colorHex);
                  return (
                    <button
                      key={occ.key}
                      type="button"
                      onClick={() => setPickedOccurrence(occ)}
                      className="flex w-full items-center gap-2 rounded-lg border border-border px-3 py-2 text-left text-sm transition-colors hover:bg-muted"
                    >
                      <span aria-hidden className="h-7 w-1 rounded-full" style={{ background: tone.solid }} />
                      <span className="flex-1">
                        <span className="font-semibold">
                          {occ.label} {occ.startAt?.toFormat("HH:mm")}–{occ.endAt?.toFormat("HH:mm")}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {names.tenant(occ.tenantId)}
                          {occ.override ? " · already overridden" : ""}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </>
            )}
          </div>
        ) : conflicts ? (
          <div className="space-y-3">
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              <span>
                {conflicts.length} booked appointment{conflicts.length === 1 ? "" : "s"} overlap this
                session on {summary?.day}. Nothing has been changed yet.
              </span>
            </div>
            {/* App convention: scroll containers are ScrollArea, never raw
                overflow-* — max-h rides on the viewport, spacing on an inner div. */}
            <ScrollArea className="pr-1" viewportClassName="max-h-44">
              <div className="space-y-1.5">
                {conflicts.map((conflict) => (
                  <ConflictRow key={conflict.id} conflict={conflict} />
                ))}
              </div>
            </ScrollArea>
            {action !== "cancel" && movableConflicts.length > 0 ? (
              <button
                type="button"
                role="switch"
                aria-checked={moveAppointments}
                onClick={() => setMoveAppointments((v) => !v)}
                className={cn(
                  "flex w-full items-start gap-2 rounded-lg border p-3 text-left text-xs transition-colors",
                  moveAppointments
                    ? "border-primary/60 bg-primary/[0.06]"
                    : "border-border hover:bg-muted/40",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border-[1.5px]",
                    moveAppointments ? "border-primary bg-primary text-primary-foreground" : "border-border",
                  )}
                >
                  {moveAppointments ? "✓" : ""}
                </span>
                <span>
                  <span className="font-semibold">
                    Also move {movableConflicts.length} appointment{movableConflicts.length === 1 ? "" : "s"} with the
                    session
                  </span>
                  <span className="block text-muted-foreground">
                    Same clock times at the new {action === "move" ? "date" : "clinic"}; every move is recorded in the
                    appointment history. Recurring appointments never move automatically.
                  </span>
                </span>
              </button>
            ) : (
              <p className="text-xs text-muted-foreground">
                These appointments stay booked — reschedule or cancel them from the calendar.
              </p>
            )}
          </div>
        ) : target ? (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm">
              <span className="font-semibold">{summary?.doctorName}</span>
              <span className="text-muted-foreground"> · {target.label} {summary?.range} · {summary?.day} · </span>
              <span className="font-medium">{summary?.tenantName}</span>
            </div>

            {existingOverride ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border p-3 text-xs">
                <span className="text-muted-foreground">
                  This day is already overridden ({existingOverride.action}
                  {existingOverride.toDate ? ` → ${existingOverride.toDate}` : ""}). Applying a new action replaces
                  it.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 [&_svg]:size-3.5"
                  disabled={submitting}
                  onClick={handleRevert}
                >
                  <RotateCcw />
                  Revert
                </Button>
              </div>
            ) : null}

            <div className="space-y-1.5">
              {ACTIONS.map((entry) => {
                const Icon = entry.icon;
                const active = action === entry.id;
                return (
                  // The picker for the active action renders OUTSIDE the card
                  // button (a fragment sibling) — DatePicker/Combobox triggers
                  // are themselves <button>s and nesting them would be invalid
                  // HTML (React hydration error).
                  <div key={entry.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setAction(entry.id);
                        setSubmitError("");
                      }}
                      className={cn(
                        "flex w-full items-start gap-2.5 rounded-lg border p-3 text-left transition-colors",
                        active ? "border-primary bg-primary/[0.06]" : "border-border hover:bg-muted/40",
                      )}
                    >
                      <Icon className={cn("mt-0.5 size-4 shrink-0", active ? "text-primary" : "text-muted-foreground")} />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold">{entry.title}</span>
                        <span className="block text-xs text-muted-foreground">{entry.description}</span>
                      </span>
                    </button>
                    {active && entry.id === "move" ? (
                      <div className="mt-1.5 pl-9">
                        <DatePicker date={toDate || defaultMoveTarget} setDate={setToDate} />
                      </div>
                    ) : null}
                    {active && entry.id === "relocate" ? (
                      <div className="mt-1.5 pl-9">
                        <ComboboxField
                          label="New clinic"
                          value={toTenantId}
                          options={tenantChoices}
                          onChange={setToTenantId}
                          placeholder="Choose clinic"
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <div className="space-y-1.5">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Note</span>
              <div className="flex flex-wrap gap-1.5">
                {NOTE_QUICK_PICKS.map((pick) => (
                  <button
                    key={pick}
                    type="button"
                    onClick={() => setNote(pick)}
                    className={cn(
                      "rounded-md border px-2 py-1 text-[11px] transition-colors",
                      note === pick
                        ? "border-primary bg-primary/10 text-foreground"
                        : "border-border text-muted-foreground hover:bg-muted",
                    )}
                  >
                    {pick}
                  </button>
                ))}
              </div>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why is this day different? (optional, shown on the roster)"
                className="min-h-16 text-sm"
              />
            </div>
          </div>
        ) : null}

        {submitError ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {submitError}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" disabled={submitting} onClick={() => onOpenChange?.(false)}>
            Cancel
          </Button>
          {conflicts ? (
            <Button type="button" size="sm" disabled={submitting} onClick={() => submit(moveAppointments ? "move" : "ignore")}>
              {submitting ? <Spinner className="size-3.5" /> : null}
              {moveAppointments ? "Apply + move appointments" : "Apply anyway"}
            </Button>
          ) : target ? (
            <Button type="button" size="sm" disabled={submitting} onClick={() => submit("abort")}>
              {submitting ? <Spinner className="size-3.5" /> : null}
              Apply override
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
